import React from "react";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  genElBoolean,
  genElDate,
  genElObject,
  genElText,
} from "../../../../components/utils/EditorDialogElementGenerator";
import KaryawanSelectorDialog from "../../../../dialogs/selectors/KaryawanSelectorDialog";
import ProblemStatusToggle from "./ProblemStatusToggle";
import {
  jenisBodyTemplate,
  kendaraanBodyTemplate,
} from "../../utils/problemTemplates";

const editTitle = "Edit Problem Kendaraan";
const addTitle = "Tambah Problem Kendaraan";

// "2026-10-14 11:52:45" -> Date (null when empty)
const toDate = (value) =>
  value ? new Date(String(value).replace(" ", "T")) : null;

const formatDateTime = (date) =>
  format(date, "d MMM yyyy, HH:mm", { locale: idLocale });

// minute precision: a few seconds apart is the same moment for these rules
const toMinute = (date) => Math.floor(date.getTime() / 60000);

// Checks the form data, pure: returns { field: message }, empty when everything is fine.
// The rules, in the order of the process (reported -> handled -> finished):
//   - Di tangani and Di tangani Tanggal go together: one of them filled means the other is required
//   - Tanggal Selesai (solved) needs somebody who handled it
//   - Di tangani Tanggal and Tanggal Selesai can't be before the problem was reported, and can't be in the future
//   - Tanggal Selesai can't be before Di tangani Tanggal
const getErrors = (data) => {
  const errors = {};
  const now = toMinute(new Date());

  const reported = toDate(data.tanggal);
  const handled = toDate(data.di_tangani_datetime);
  const solved = toDate(data.tanggal_selesai);

  if (!data.di_tangani_id && (data.di_tangani_datetime || data.tanggal_selesai)) {
    errors["data_di_tangani.nama"] = data.tanggal_selesai
      ? "Pilih siapa yang menangani untuk menandai selesai"
      : "Pilih siapa yang menangani";
  }
  if (data.di_tangani_id && !data.di_tangani_datetime) {
    errors.di_tangani_datetime = "Isi kapan ditangani";
  }

  const checkRange = (field, date, label) => {
    if (!date) return;
    if (reported && toMinute(date) < toMinute(reported)) {
      errors[field] = `${label} tidak boleh sebelum problem dilaporkan (${formatDateTime(
        reported
      )})`;
    } else if (toMinute(date) > now) {
      errors[field] = `${label} tidak boleh di masa depan`;
    }
  };
  checkRange("di_tangani_datetime", handled, "Tanggal ditangani");
  checkRange("tanggal_selesai", solved, "Tanggal selesai");

  if (
    !errors.tanggal_selesai &&
    solved &&
    handled &&
    toMinute(solved) < toMinute(handled)
  ) {
    errors.tanggal_selesai = `Tanggal selesai tidak boleh sebelum ditangani (${formatDateTime(
      handled
    )})`;
  }

  return errors;
};

// A read-only look at the problem that is edited: the problem itself can't be changed here, only how it is handled.
const ProblemSummary = ({ data, groupText }) => {
  const reported = toDate(data.tanggal);

  return (
    <div className="col-start-start w-100 gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="row-between-center w-100 gap-2 flex-wrap">
        <span className="font-bold text-slate-800">
          {kendaraanBodyTemplate(data)}
          <span className="ml-2 text-sm font-normal text-slate-500">
            {jenisBodyTemplate(data)}
          </span>
        </span>
        {reported && (
          <span className="text-xs text-slate-500">
            Dilaporkan {formatDateTime(reported)}
          </span>
        )}
      </div>
      <div className="row-start-center gap-2 flex-wrap">
        {groupText && (
          <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-600">
            {groupText}
          </span>
        )}
        <span className="text-sm text-slate-700">{data.keterangan || "-"}</span>
      </div>
    </div>
  );
};

const ProblemEditorDialog = (props) => {
  const {
    visible,
    onHide,
    onConfirm,
    loading,
    data,
    setData: setDataOfPage,
    addNewMode,
    error,
    setError,
    formatLabelGrup,
  } = props;

  // dialog state
  const [showPnjSelector, setShowPnjSelector] = React.useState(false);

  // Everything of this dialog changes the data through here.
  // Tanggal Selesai filled in: the problem is solved (status 3).
  // Tanggal Selesai emptied on a solved problem: it is not fixed after all, back to schedule (status 2).
  // Once a save was refused the messages follow the data, so a message goes away when it is fixed.
  const setData = (newData) => {
    if (newData.tanggal_selesai !== data.tanggal_selesai) {
      if (newData.tanggal_selesai) {
        newData = { ...newData, status: 3 };
      } else if (data.status === 3) {
        newData = { ...newData, status: 2 };
      }
    }
    setDataOfPage(newData);
    if (Object.keys(error).length > 0) setError(getErrors(newData));
  };

  // Schedule / Ignore button: the problem starts over with that status, so Tanggal Selesai and
  // who / when handled it (Di tangani, Di tangani Tanggal) are emptied. (Tanggal Selesai would also make it solved again.)
  const handleOnChangeStatus = (status) => {
    setDataOfPage({
      ...data,
      status,
      tanggal_selesai: null,
      di_tangani_id: null,
      di_tangani_datetime: null,
      data_di_tangani: null,
    });
    setError({});
  };

  const validate = () => {
    const errors = getErrors(data);
    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOnConfirmPnj = (event, datas) => {
    let selected;
    event.preventDefault();
    if (datas && datas.length > 0) {
      selected = datas[0];
      setData({
        ...data,
        data_di_tangani: selected,
        di_tangani_id: selected.id,
        // picking who handled it starts with now, it can be changed
        di_tangani_datetime:
          data.di_tangani_datetime ?? format(new Date(), "yyyy-MM-dd HH:mm:ss"),
      });
      setShowPnjSelector(false);
    }
    return selected;
  };

  // nobody handled it: the date of handling goes with it
  const handleOnClearPnj = () => {
    setData({
      ...data,
      data_di_tangani: null,
      di_tangani_id: null,
      di_tangani_datetime: null,
    });
  };

  const today = new Date();
  const reportedDate = toDate(data.tanggal) ?? undefined;

  const elDitangani = genElObject({
    field: "data_di_tangani.nama",
    name: "Ditangani oleh",
    value: data.data_di_tangani ? data.data_di_tangani.nama : "",
    onClick: () => setShowPnjSelector(true),
    onClear: data.di_tangani_id ? handleOnClearPnj : undefined,
    error,
  });
  const elDitanganiTanggal = genElDate({
    field: "di_tangani_datetime",
    name: "Tanggal ditangani",
    data,
    setData,
    error,
    showTime: true,
    min: reportedDate,
    max: today,
  });
  const elTanggalSelesai = genElDate({
    field: "tanggal_selesai",
    name: "Tanggal selesai",
    data,
    setData,
    error,
    showTime: true,
    min: reportedDate,
    max: today,
  });
  const elNote = genElText({
    field: "note",
    name: "Note",
    data,
    setData,
    error,
  });
  const elShowDashboard = genElBoolean({
    field: "show_dashboard",
    name: "Tampilkan di dashboard",
    data,
    setData,
    error,
    labelTrue: "Ya",
    labelFalse: "Tidak",
  });

  const dialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        outlined
        onClick={props.onHide}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={() => {
          if (validate()) {
            // with a Tanggal Selesai the problem is solved
            onConfirm(data.tanggal_selesai ? { ...data, status: 3 } : data);
          }
        }}
      />
    </React.Fragment>
  );

  const groupText =
    data.label_grup && formatLabelGrup ? formatLabelGrup(data.label_grup) : "";

  return (
    <Dialog
      visible={visible}
      style={{ width: "40rem" }}
      breakpoints={{ "960px": "75vw", "641px": "90vw" }}
      header={addNewMode ? addTitle : editTitle}
      modal
      className={`p-fluid`}
      footer={dialogFooter}
      onHide={onHide}
    >
      <KaryawanSelectorDialog
        title="Pilih PNJ"
        visible={showPnjSelector}
        onHide={() => setShowPnjSelector(false)}
        onConfirm={handleOnConfirmPnj}
        additionalFilter={"jabatan_id:=:3"}
      />

      <div className="col-start-center relative h-100 gap-4">
        <ProblemSummary data={data} groupText={groupText} />

        <ProblemStatusToggle
          status={data.status}
          onChange={handleOnChangeStatus}
        />

        <div className="col-start-start w-100 gap-3">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Penanganan
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-100">
            {elDitangani}
            {elDitanganiTanggal}
            {elTanggalSelesai}
            {elShowDashboard}
          </div>
          {elNote}
        </div>

        {loading && (
          <div
            className={`col-center-center w-100  gap-2 absolute z-100 h-100 bg-black/20 rounded `}
          >
            <ProgressSpinner />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ProblemEditorDialog;
